import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreatePlaylist, usePlaylists, useAddCourseToPlaylist } from "@/hooks/usePlaylists";
import { useAppStore } from "@/lib/store";

function PlusCircleIcon() {
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="23" cy="23" r="22.5" stroke="#E2E4E9" />
      <path d="M22.25 22.25V16.75H23.75V22.25H29.25V23.75H23.75V29.25H22.25V23.75H16.75V22.25H22.25Z" fill="#525866" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CreatePerformanceModal() {
  const { activeModal, closeModal, selectedAddToPlaylistCourseId, setSelectedAddToPlaylistCourseId } = useAppStore();
  const { execute: createPlaylist, loading } = useCreatePlaylist();
  const { execute: addCourseToPlaylist } = useAddCourseToPlaylist();
  const { refetch: refetchPlaylists } = usePlaylists();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (activeModal !== "create-performance") return null;

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setError(null);
    const result = await createPlaylist(trimmed);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    const newPlaylistId = result.data?.id;

    // Auto-add the selected course if one was pending
    if (newPlaylistId && selectedAddToPlaylistCourseId) {
      await addCourseToPlaylist(newPlaylistId, selectedAddToPlaylistCourseId);
      setSelectedAddToPlaylistCourseId(null);
    }

    setName("");
    refetchPlaylists();
    closeModal();

    if (newPlaylistId) {
      navigate(`/app/dashboard/playlist?playlist_id=${newPlaylistId}`);
    }
  };

  return (
    /* create_playlist_component */
    <div className="fixed inset-0 z-[100] flex items-center justify-center max-[767px]:px-[5%]">
      {/* share_modal_background-overlay */}
      <div
        className="absolute inset-0 z-[-1] bg-black/25"
        onClick={closeModal}
      />

      {/* create_playlist_content-wrapper */}
      <div className="relative w-full max-w-[32rem] overflow-x-auto rounded-[0.75rem] bg-white max-[991px]:w-[90%] max-[991px]:max-w-none">
        {/* playlist_modal_card_header */}
        <div className="flex items-center gap-4 border-b border-[#e2e4e9] px-5 py-5 pb-4">
          <PlusCircleIcon />
          <div>
            <div className="text-base font-medium leading-[1.2]">
              Create New Performance
            </div>
          </div>
        </div>

        {/* playlist_modal_form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* playlist_modal_input-wrapper */}
          <div className="flex flex-col gap-1 border-b border-[#e2e4e9] px-5 py-5">
            <label className="font-[family-name:Inter,sans-serif] text-[0.875rem] text-[#0a0d14]">
              Enter Title
            </label>
            <input
              type="text"
              value={name}
              placeholder="Alex Sangeet Performance"
              className="w-full rounded-[10px] border border-[#e2e4e9] bg-transparent px-4 text-[0.875rem] text-[#4a193c] outline-0 placeholder:text-[#868c98] placeholder:text-[0.875rem]"
              style={{ minHeight: "44px" }}
              autoFocus
              required
              onChange={(e) => setName(e.target.value)}
            />
            {error && (
              <p className="mt-1 text-[0.875rem] text-[#ba4d4d]">{error}</p>
            )}
          </div>

          {/* playlist_modal_btn-wrapper */}
          <div className="grid grid-cols-2 gap-4 px-5 py-5 max-[767px]:flex max-[767px]:flex-col">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-[0.5rem] border border-[#e2e4e9] bg-transparent px-6 py-4 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[5%] active:scale-95"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-[0.5rem] bg-[#762a5c] px-6 py-4 font-[family-name:Inter,sans-serif] text-[0.875rem] font-medium leading-none text-white transition-transform duration-200 hover:-translate-y-[5%] active:scale-95 disabled:opacity-50"
              disabled={loading || !name.trim()}
            >
              {loading ? "Creating..." : "Create Performance"}
            </button>
          </div>
        </form>

        {/* modal_close-button */}
        <button
          type="button"
          className="absolute right-2 top-2 flex text-black"
          aria-label="Close"
          onClick={closeModal}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
